/**
 * SearchFilterOverlay Component
 *
 * Main wrapper component that orchestrates the condensed search trigger,
 * expandable overlay with filters, and applied filter pills display.
 *
 * Design follows Polaris depth guidelines:
 * - Uses Popover for built-in elevation, shadow, and accessibility
 * - Flat layout with minimal visual boundaries
 * - Closes on click outside, Escape, or close button
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Popover,
  Box,
  BlockStack,
  InlineStack,
  TextField,
  Text,
  Button,
  Icon,
  Badge,
  ChoiceList,
  Divider,
  Tooltip,
} from '@shopify/polaris';
import {
  SearchIcon,
  AlertTriangleIcon,
  AlertCircleIcon,
  InfoIcon,
  XIcon,
  FilterIcon,
} from '@shopify/polaris-icons';
import type { AlertSeverity } from '../../types/dashboard';
import type { AutocompleteOption } from '../../types/search';

/**
 * Props for SearchFilterOverlay component
 */
export interface SearchFilterOverlayProps {
  // Search
  searchQuery: string;
  onSearchChange: (value: string) => void;
  suggestions?: AutocompleteOption[];
  placeholder?: string;

  // Alert Filters
  severityFilter: AlertSeverity[];
  onSeverityChange: (values: AlertSeverity[]) => void;
  daysMin: string;
  daysMax: string;
  onDaysMinChange: (value: string) => void;
  onDaysMaxChange: (value: string) => void;
  daysRangeError: boolean;
  inventoryMin: string;
  inventoryMax: string;
  onInventoryMinChange: (value: string) => void;
  onInventoryMaxChange: (value: string) => void;
  inventoryRangeError: boolean;

  // Product Filters
  statusFilter: string[];
  onStatusChange: (values: string[]) => void;
  productTypeFilter: string[];
  onProductTypeChange: (values: string[]) => void;
  productVendorFilter: string[];
  onProductVendorChange: (values: string[]) => void;
  productTypeOptions: string[];
  productVendorOptions: string[];

  // Clear handlers
  onClearAllFilters: () => void;

  // Filter counts for badge
  totalFilterCount: number;

  // Expansion state callback for parent wrapper animation
  onExpandChange?: (isExpanded: boolean) => void;
}

/**
 * Severity filter configuration - matches app-wide badge patterns
 */
const SEVERITY_CONFIG = {
  critical: {
    label: 'Critical',
    tone: 'critical' as const,
    icon: AlertTriangleIcon,
  },
  warning: {
    label: 'Warning',
    tone: 'warning' as const,
    icon: AlertCircleIcon,
  },
  info: {
    label: 'Info',
    tone: 'info' as const,
    icon: InfoIcon,
  },
} as const;

/**
 * Product status options for filtering
 * Maps to MonitoredProductsTable status badges
 */
const PRODUCT_STATUS_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'healthy', label: 'Healthy' },
  { value: 'pending', label: 'Pending data' },
];

/**
 * SearchFilterOverlay - Main component
 */
export function SearchFilterOverlay({
  searchQuery,
  onSearchChange,
  placeholder = 'Search products, SKUs...',
  severityFilter,
  onSeverityChange,
  daysMin,
  daysMax,
  onDaysMinChange,
  onDaysMaxChange,
  daysRangeError,
  inventoryMin,
  inventoryMax,
  onInventoryMinChange,
  onInventoryMaxChange,
  inventoryRangeError,
  statusFilter,
  onStatusChange,
  productTypeFilter,
  onProductTypeChange,
  productVendorFilter,
  onProductVendorChange,
  productTypeOptions,
  productVendorOptions,
  onClearAllFilters,
  totalFilterCount,
  onExpandChange,
}: SearchFilterOverlayProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Open the overlay with animation
  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setIsClosing(false);
    onExpandChange?.(true);
    
    // Focus search input after animation starts
    setTimeout(() => {
      const input = overlayRef.current?.querySelector('input[type="text"]') as HTMLInputElement;
      input?.focus();
    }, 100);
  }, [onExpandChange]);

  // Close the overlay with animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    onExpandChange?.(false);

    // Wait for close animation to complete before hiding (matches CSS animation duration)
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 150);
  }, [onExpandChange]);

  // Keyboard handler for Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isClosing) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isClosing, handleClose]);

  // Handle severity change with proper typing
  const handleSeverityChange = useCallback(
    (values: string[]) => {
      onSeverityChange(values as AlertSeverity[]);
    },
    [onSeverityChange]
  );

  // Check if any filters are active
  const hasActiveFilters = totalFilterCount > 0;

  // Product type choices
  const productTypeChoices = useMemo(
    () => productTypeOptions.map((opt) => ({ value: opt, label: opt })),
    [productTypeOptions]
  );

  // Product vendor choices
  const productVendorChoices = useMemo(
    () => productVendorOptions.map((opt) => ({ value: opt, label: opt })),
    [productVendorOptions]
  );

  // Search button activator
  const activator = (
    <div className="search-filter-activator">
      <div className="search-filter-button-wrapper">
        <Tooltip content="Search and filter monitoring data">
          <Button
            icon={FilterIcon}
            onClick={handleOpen}
            pressed={isOpen}
            accessibilityLabel={hasActiveFilters ? `Open search and filters (${totalFilterCount} active)` : 'Open search and filters'}
          >
            Filters
          </Button>
        </Tooltip>
        {hasActiveFilters && (
          <span className="search-filter-badge">
            {totalFilterCount}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <Popover
      active={isOpen}
      activator={activator}
      onClose={handleClose}
      preferredPosition="below"
      preferredAlignment="right"
      sectioned={false}
      autofocusTarget="none"
      fluidContent
    >
      <div 
        className={`search-filter-overlay${isClosing ? ' search-filter-overlay--closing' : ''}`}
        ref={overlayRef}
      >
        {/* Header */}
        <div className="search-filter-header">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h3" variant="headingSm" fontWeight="semibold">
                Filters
              </Text>
              {hasActiveFilters && (
                <Badge tone="info" size="small">
                  {String(totalFilterCount)}
                </Badge>
              )}
            </InlineStack>
            <Button
              icon={XIcon}
              variant="tertiary"
              onClick={handleClose}
              accessibilityLabel="Close filters"
            />
          </InlineStack>
        </div>

        {/* Scrollable content area */}
        <div className="search-filter-content">
          <BlockStack gap="400">
            {/* Search input */}
            <TextField
              label="Search"
              labelHidden
              value={searchQuery}
              onChange={onSearchChange}
              placeholder={placeholder}
              prefix={<Icon source={SearchIcon} tone="subdued" />}
              clearButton
              onClearButtonClick={() => onSearchChange('')}
              autoComplete="off"
            />

            {/* Severity filter */}
            <BlockStack gap="200">
              <Text as="span" variant="bodySm" fontWeight="medium">
                Severity
              </Text>
              <InlineStack gap="200" wrap>
                {(['critical', 'warning', 'info'] as const).map((severity) => {
                  const isSelected = severityFilter.includes(severity);
                  const config = SEVERITY_CONFIG[severity];
                  return (
                    <button
                      key={severity}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          handleSeverityChange(severityFilter.filter((s) => s !== severity));
                        } else {
                          handleSeverityChange([...severityFilter, severity]);
                        }
                      }}
                      className={`severity-badge-button ${isSelected ? 'severity-badge-button--selected' : ''}`}
                      aria-pressed={isSelected}
                    >
                      <Badge tone={config.tone} icon={config.icon}>
                        {config.label}
                      </Badge>
                    </button>
                  );
                })}
              </InlineStack>
            </BlockStack>

            {/* Days to stockout range */}
            <BlockStack gap="200">
              <Text as="span" variant="bodySm" fontWeight="medium">
                Days to stockout
              </Text>
              <InlineStack gap="200" blockAlign="center" wrap={false}>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Min days"
                    labelHidden
                    value={daysMin}
                    onChange={onDaysMinChange}
                    placeholder="Min"
                    type="number"
                    min={0}
                    autoComplete="off"
                    error={daysRangeError}
                  />
                </div>
                <Text as="span" variant="bodySm" tone="subdued">to</Text>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Max days"
                    labelHidden
                    value={daysMax}
                    onChange={onDaysMaxChange}
                    placeholder="Max"
                    type="number"
                    min={0}
                    autoComplete="off"
                    error={daysRangeError}
                  />
                </div>
              </InlineStack>
            </BlockStack>

            {/* Inventory level range */}
            <BlockStack gap="200">
              <Text as="span" variant="bodySm" fontWeight="medium">
                Inventory level
              </Text>
              <InlineStack gap="200" blockAlign="center" wrap={false}>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Min inventory"
                    labelHidden
                    value={inventoryMin}
                    onChange={onInventoryMinChange}
                    placeholder="Min"
                    type="number"
                    min={0}
                    autoComplete="off"
                    error={inventoryRangeError}
                  />
                </div>
                <Text as="span" variant="bodySm" tone="subdued">to</Text>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Max inventory"
                    labelHidden
                    value={inventoryMax}
                    onChange={onInventoryMaxChange}
                    placeholder="Max"
                    type="number"
                    min={0}
                    autoComplete="off"
                    error={inventoryRangeError}
                  />
                </div>
              </InlineStack>
            </BlockStack>

            {/* Status filter */}
            <ChoiceList
              title="Status"
              choices={PRODUCT_STATUS_OPTIONS}
              selected={statusFilter}
              onChange={onStatusChange}
              allowMultiple
            />

            {/* Product Type filter */}
            {productTypeChoices.length > 0 && (
              <ChoiceList
                title="Product type"
                choices={productTypeChoices}
                selected={productTypeFilter}
                onChange={onProductTypeChange}
                allowMultiple
              />
            )}

            {/* Vendor filter */}
            {productVendorChoices.length > 0 && (
              <ChoiceList
                title="Vendor"
                choices={productVendorChoices}
                selected={productVendorFilter}
                onChange={onProductVendorChange}
                allowMultiple
              />
            )}
          </BlockStack>
        </div>

        {/* Footer actions */}
        <div className="search-filter-footer">
          <InlineStack align="space-between" blockAlign="center">
            <Button 
              onClick={onClearAllFilters} 
              disabled={!hasActiveFilters}
              variant="plain"
              tone="critical"
            >
              Clear all
            </Button>
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          </InlineStack>
        </div>
      </div>
    </Popover>
  );
}

export default SearchFilterOverlay;
